import { useApiMutation } from "@gladis/api-client";
import { Button, Label, MoneyInput, TextField, parseEurInput } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import { PROPERTIES_KEY } from "./PropertyList.js";

/**
 * Record what a property is worth now (FR-012 to FR-019).
 *
 * APPENDS. It never edits. The form does not load an existing entry, offers no way to pick one, and
 * the endpoint it posts to has no PATCH or DELETE sibling at any address. A correction is another
 * submission of this same form with a newer date, and the previous entry stays in the history.
 *
 * THE FUTURE-DATED WARNING COMES FROM THE SERVER, not from a comparison here. The rule is "later
 * than the server's today", and a browser in another timezone gets that wrong on exactly the day it
 * matters. Deciding it locally would also mean two implementations of one rule — the failure mode
 * this feature has spent real effort avoiding elsewhere.
 *
 * Back-dating is silent. It is the supported way to enter history, and warning about it would train
 * people to dismiss warnings.
 */
export function ValuationForm({
  propertyId,
  onRecorded,
}: {
  propertyId: string;
  onRecorded?: () => void;
}): ReactNode {
  const t = useT();
  const [value, setValue] = useState("");
  const [valuationDate, setValuationDate] = useState(() => new Date().toISOString().slice(0, 10));

  const record = useApiMutation<
    { valuationId: string; warning: string | null },
    Record<string, unknown>
  >(
    (client, input) =>
      client.post<{ valuationId: string; warning: string | null }>(
        `/properties/${propertyId}/valuations`,
        input,
      ),
    { invalidates: [PROPERTIES_KEY] },
  );

  const amount = parseEurInput(value);

  function submit(event: FormEvent): void {
    event.preventDefault();
    if (amount === null) return;

    record.mutate(
      { value: amount, valuationDate },
      {
        onSuccess: () => {
          setValue("");
          onRecorded?.();
        },
      },
    );
  }

  return (
    /*
     * NO CARD CHROME. This form is rendered inside a dialog, which is already a surface — a bordered
     * panel within a modal is a card inside a card, with two borders and doubled padding.
     *
     * A form does not know where it is rendered and should not decide that it is a card. The caller
     * does: the dialog supplies its own surface, and the one place this is used inline wraps it in a
     * `Panel` the same way every other section is wrapped.
     */
    <form onSubmit={submit} className="flex flex-col gap-4">
      {/*
       * NO HEADING AND NO DESCRIPTION. The dialog supplies both — they are its accessible name and
       * description — and repeating them here rendered the same two sentences twice, once as the
       * modal's title and once as the form's.
       */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="valuation-value">{t("prop.list.colValue")}</Label>
          <MoneyInput id="valuation-value" value={value} onValueChange={setValue} required />
        </div>

        <div>
          <Label htmlFor="valuation-date">{t("prop.form.asOf")}</Label>
          <TextField
            id="valuation-date"
            type="date"
            value={valuationDate}
            onChange={(event) => setValuationDate(event.target.value)}
            required
          />
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <Button type="submit" variant="primary" disabled={amount === null || record.isPending}>
          {record.isPending ? t("common.saving") : t("prop.valuation.record")}
        </Button>

        {record.data?.warning === "future-dated" ? (
          <span className="text-sm text-muted">{t("prop.valuation.futureSaved")}</span>
        ) : null}

        {record.isError ? (
          <span className="text-sm text-danger">{errorSentence(record.error)}</span>
        ) : null}
      </div>
    </form>
  );
}
